// MARK://dhk8/admin-shell/lib/api
// Thin fetch wrapper for the merchant admin. Backends are reached at
// `${apiBase()}/<service>/v1/...` in cloud-direct mode (api.dockethub.app/v1) — the
// per-service path segment maps to the ingress route for each dhk8-* service.
// Tenant + actor context travel as headers (`x-dhk8-tenant-id` / `x-dhk8-user-id`)
// and, when present, a bearer JWT — same as the POS app.

import { apiBase } from './api-origin.js'
import { authSession, getStoredRefreshToken, applyRefreshedToken } from '../stores/auth-store.js'

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  body?: unknown
}

/** Build a versioned URL for a dhk8-* service, e.g.
 *  svcUrl('dhk8-catalog-menus', '/menus') → https://api.dockethub.app/v1/dhk8-catalog-menus/menus */
export function svcUrl(service: string, path = ''): string {
  const p = path.startsWith('/') ? path : `/${path}`
  return `${apiBase()}/${service}${p}`
}

// ── Single-flight refresh ───────────────────────────────────────────────────
// El refresh token es de UN SOLO USO (rota en cada uso). Si varias requests
// 401ean en paralelo al vencer el access token (15min), TODAS intentarían
// refrescar con el mismo refresh token → solo la primera gana y las demás
// quedan 401 (esto rompía /menus y otras páginas que disparan varias llamadas
// a la vez). Por eso compartimos UN refresh en vuelo: la primera lo dispara,
// las demás esperan el mismo Promise y reintentan con el token nuevo.
let refreshInFlight: Promise<string | null> | null = null

async function doRefresh(): Promise<string | null> {
  const refreshTok = getStoredRefreshToken()
  if (!refreshTok) return null
  try {
    const rr = await fetch(svcUrl('dhk8-platform-auth', '/api/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refreshTok }),
      credentials: 'include',
    })
    if (!rr.ok) return null
    const raw = (await rr.json()) as
      | { ok: true; data: { token?: string; refreshToken?: string } }
      | { token?: string; refreshToken?: string }
    const d = raw && typeof raw === 'object' && 'data' in raw ? raw.data : (raw as { token?: string; refreshToken?: string })
    if (d && typeof d.token === 'string' && d.token.length > 0) {
      applyRefreshedToken(d.token, d.refreshToken)
      return d.token
    }
    return null
  } catch {
    return null
  }
}

/** Garantiza un único refresh concurrente; devuelve el access token nuevo o null.
 *  Exportado para que el cliente GraphQL (lib/gql.ts) reuse el MISMO single-flight. */
export function ensureRefreshed(): Promise<string | null> {
  if (refreshInFlight === null) {
    refreshInFlight = doRefresh().finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

export async function apiFetch<T>(url: string, init?: ApiRequestInit): Promise<T> {
  const sess = authSession()
  const rawBody = init?.body
  const body: BodyInit | null | undefined =
    rawBody === undefined || rawBody === null
      ? undefined
      : typeof rawBody === 'string' || rawBody instanceof FormData || rawBody instanceof URLSearchParams || rawBody instanceof Blob || rawBody instanceof ArrayBuffer
        ? (rawBody as BodyInit)
        : JSON.stringify(rawBody)
  // Only advertise a JSON content-type when we actually send a JSON body.
  // Fastify rejects an empty body with `Content-Type: application/json`
  // (FST_ERR_CTP_EMPTY_JSON_BODY) — that broke DELETE/no-body POSTs.
  const isJsonBody = body !== undefined && body !== null
    && !(rawBody instanceof FormData) && !(rawBody instanceof URLSearchParams)
    && !(rawBody instanceof Blob) && !(rawBody instanceof ArrayBuffer)
  const headers: Record<string, string> = {
    ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
    ...(sess?.tenantId ? { 'x-dhk8-tenant-id': sess.tenantId } : {}),
    ...(sess?.userId ? { 'x-dhk8-user-id': sess.userId } : {}),
    ...(sess?.token ? { Authorization: `Bearer ${sess.token}` } : {}),
    ...(init?.headers as Record<string, string> | undefined),
  }

  let res = await fetch(url, { ...init, headers, body, credentials: 'include' })

  // 2026-05-31 — rotación transparente del access token (mirror del POS) con
  // single-flight (ver doRefresh/ensureRefreshed arriba). El JWT del admin
  // expira a 15min; sin esto, cualquier 401 rompía la sesión hasta re-login
  // (modifiers/menus/reportes → "tenant scope required — present a valid JWT").
  if (res.status === 401 && !url.includes('/auth/refresh')) {
    const newToken = await ensureRefreshed()
    if (newToken) {
      const retryHeaders: Record<string, string> = { ...headers, Authorization: `Bearer ${newToken}` }
      res = await fetch(url, { ...init, headers: retryHeaders, body, credentials: 'include' })
    }
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const data = await res.json()
      msg = data?.message ?? data?.error?.message ?? data?.error ?? msg
    } catch { /* ignore */ }
    throw new ApiError(res.status, msg)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** Unwrap the `{ ok: true, data } | { ok: false, error }` envelope some services use. */
export async function apiData<T>(url: string, init?: ApiRequestInit): Promise<T> {
  const body = await apiFetch<{ ok: true; data: T } | { ok: false; error: { code: string; message: string } } | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as { ok: true; data: T } | { ok: false; error: { code: string; message: string } }
    if (env.ok) return env.data
    throw new ApiError(400, `${env.error.code}: ${env.error.message}`)
  }
  return body as T
}

// ─────────────────────────────────────────────────────────────────────────
// 2026-05-24 hotfix — tenant guard pre-fetch
//
// Por qué: muchos clientes construyen URLs como `?tenantId=${encodeURIComponent(authTenantId())}`.
// Cuando la sesión aún no está hidratada (primer mount, race con loadAccount),
// `authTenantId()` devuelve `''` (cadena vacía) → la query queda `?tenantId=`
// que algunos backends pasan directo a Prisma → `invalid input syntax for type
// uuid: ""`. La UX se rompe con un error críptico cuando lo correcto es
// esperar al render siguiente sin disparar la request.
//
// Uso: en los clientes, llamar `requireTenant()` al inicio. Si devuelve null,
// retornar early con un dataset vacío para que la UI muestre su empty state
// limpio. El `useAsync` hook re-disparará el fetcher cuando la sesión cargue
// porque su dep (authTenantId) cambia.
// ─────────────────────────────────────────────────────────────────────────
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-7][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/** Devuelve el tenantId actual si es un UUID válido; null si está vacío o sin formato. */
export function requireTenant(): string | null {
  const sess = authSession()
  const t = sess?.tenantId ?? ''
  if (t.length === 0 || !UUID_RE.test(t)) return null
  return t
}

/** Helper para clientes que devuelven listas: retorna shape `{ items, total, ... }` vacío si no hay tenant. */
export function emptyListResult<T>(): { items: T[]; total: number; page: number; pageSize: number } {
  return { items: [], total: 0, page: 1, pageSize: 0 }
}
